import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { usePrintConfigImages } from '@/hooks/usePrintConfigImages';

interface DocumentData {
  order: any;
  materials: any[];
  expectedResults: any | null;
  tenantLogo?: string;
}

interface ProductionOrderDocumentProps {
  orderId: string;
  printContentId?: string;
}

export default function ProductionOrderDocument({ orderId, printContentId = 'print-content' }: ProductionOrderDocumentProps) {
  const { urlFor } = usePrintConfigImages();
  const [data, setData] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [pageCount, setPageCount] = useState(1);

  // Letter page (11in) minus 0.4in top/bottom @page margin, at 96 CSS px/in
  const PAGE_CONTENT_HEIGHT_PX = 979;

  // Print isolation: `visibility: hidden` (used below to hide everything
  // except this element) removes content from VIEW but not from LAYOUT —
  // hidden siblings still occupy their normal-flow height. Since this
  // element stays in normal flow (no more `position: absolute`), the fix
  // below removes those siblings from layout entirely (`display: none`)
  // for the duration of the print job, so the page has nothing but this
  // document to paginate.
  //
  // That alone isn't sufficient when this component is rendered inside the
  // "Vista Previa de Impresión" modals (ProductionOrders.tsx,
  // PlantExecution.tsx): those modals' own wrapper elements — not siblings,
  // ANCESTORS of this element — carry `position: fixed` (the full-screen
  // overlay), `max-height: 95vh; overflow: auto` (the inner panel), and
  // (once inside AppLayout) a `padding-left` offset reserved for the fixed
  // sidebar. Confirmed via a Playwright print-to-PDF reproduction of the
  // exact same markup/CSS: identical document content rendered through the
  // standalone /print route (no such ancestors) paginates correctly across
  // as many pages as needed, while rendered through the modal it either
  // gets hard-clipped to a single page (overflow/max-height) or shifted
  // into a huge dead left margin (the sidebar's padding-left, still active
  // since print doesn't strip `lg:` breakpoint classes) — matching the
  // "Sección 5 truncada"/"trunca la información entre hojas" reports for
  // Órdenes de Producción and Ejecución de Planta (ROT-154). Fix: walk the
  // same ancestor chain used to hide siblings and unconditionally neutralize
  // every box-model property on each ancestor that could constrain or
  // offset this element's print layout, restoring the saved inline styles
  // after printing. Applying this to every ancestor regardless of its
  // current styles is deliberate — this element is the only thing left
  // visible inside any of them at print time, so there's no layout
  // (screen-only) cost to zeroing box-model properties across the board,
  // and it avoids re-litigating which specific property was the culprit.
  useEffect(() => {
    let hiddenSiblings: HTMLElement[] = [];
    let unclampedAncestors: Array<{ el: HTMLElement; style: string }> = [];

    const isolateForPrint = () => {
      const target = rootRef.current;
      if (!target) return;
      let node: HTMLElement | null = target;
      while (node && node !== document.body) {
        const parent: HTMLElement | null = node.parentElement;
        if (parent) {
          Array.from(parent.children).forEach((sibling) => {
            if (sibling !== node && sibling instanceof HTMLElement && sibling.style.display !== 'none') {
              sibling.setAttribute('data-print-isolated-hidden', 'true');
              hiddenSiblings.push(sibling);
            }
          });

          unclampedAncestors.push({ el: parent, style: parent.getAttribute('style') || '' });
          Object.assign(parent.style, {
            position: 'static', overflow: 'visible', maxHeight: 'none', maxWidth: 'none',
            height: 'auto', width: 'auto', margin: '0', padding: '0',
            top: 'auto', left: 'auto', right: 'auto', bottom: 'auto', inset: 'auto',
          });
        }
        node = parent;
      }
      hiddenSiblings.forEach((el) => { el.style.display = 'none'; });
    };

    const restoreAfterPrint = () => {
      hiddenSiblings.forEach((el) => {
        el.style.display = '';
        el.removeAttribute('data-print-isolated-hidden');
      });
      hiddenSiblings = [];
      unclampedAncestors.forEach(({ el, style }) => el.setAttribute('style', style));
      unclampedAncestors = [];
    };

    window.addEventListener('beforeprint', isolateForPrint);
    window.addEventListener('afterprint', restoreAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', isolateForPrint);
      window.removeEventListener('afterprint', restoreAfterPrint);
      restoreAfterPrint();
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const tenantSlug = localStorage.getItem('tenantSlug') || 'demo-corp';
        const config = { headers: { Authorization: `Bearer ${token}` } };

        const [orderRes, materialsRes, expectedRes] = await Promise.all([
          axios.get(`/api/production-orders/${orderId}`, config),
          axios.get(`/api/production-orders/${orderId}/materials`, config),
          axios.get(`/api/production-orders/${orderId}/expected-results`, config).catch(() => ({ data: null })),
        ]);

        let tenantLogo: string | undefined;
        try {
          const logoRes = await axios.get(`/api/tenants/${tenantSlug}/logo`, config);
          if (logoRes.data?.logoUrl) tenantLogo = logoRes.data.logoUrl;
        } catch (e) {}

        setData({
          order: orderRes.data,
          materials: materialsRes.data || [],
          expectedResults: expectedRes.data || null,
          tenantLogo,
        });
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };
    if (orderId) fetchData();
  }, [orderId]);

  // Estimate how many printed pages the content will span, so we can render
  // a "Página X de Y" + folio footer on every page. Recomputes whenever the
  // rendered height changes (e.g. logo or print-config image finishes loading).
  useEffect(() => {
    if (!data || !contentRef.current) return;
    const el = contentRef.current;
    const update = () => setPageCount(Math.max(1, Math.ceil(el.scrollHeight / PAGE_CONTENT_HEIGHT_PX)));
    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando datos para impresión...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-red-600">{error || 'No se encontraron datos'}</p>
      </div>
    );
  }

  const { order, materials, expectedResults, tenantLogo } = data;

  let parsedNotes: any = {};
  try { if (order.notes) parsedNotes = JSON.parse(order.notes); } catch (e) {}
  const processSpecs = parsedNotes.processSpecs || {};
  const colorRows: Array<{ colorBase: string; pantone: string }> = processSpecs.colorRows || [];
  const activeColorRows = colorRows.filter(r => r.colorBase || r.pantone);

  const structureWeight = expectedResults?.structureWeight ?? processSpecs.structureWeight ?? null;
  const rendimiento = expectedResults?.yield ?? processSpecs.rendimiento ?? null;
  const factorDesperdicio = expectedResults?.expectedFactor ?? processSpecs.wasteFactor ?? null;

  const SectionHeader = ({ num, title }: { num: string; title: string }) => (
    <div className="bg-gray-800 text-white text-xs font-bold px-3 py-1.5 mb-2 uppercase tracking-wide">
      {num}. {title}
    </div>
  );

  const SubSectionHeader = ({ num, title }: { num: string; title: string }) => (
    <div className="bg-gray-200 text-gray-800 text-xs font-bold px-3 py-1 mb-2 border-l-4 border-gray-800 uppercase tracking-wide">
      {num}. {title}
    </div>
  );

  const Row = ({ label, value }: { label: string; value?: string | number | null }) => (
    <div className="flex items-baseline gap-1 mb-0.5 text-xs">
      <span className="font-semibold text-gray-700 whitespace-nowrap">{label}:</span>
      <span className="text-gray-900">{value || '—'}</span>
    </div>
  );

  const BigRow = ({ label, value }: { label: string; value?: string | number | null }) => (
    <div className="flex items-baseline gap-2 mb-1.5">
      <span className="font-bold text-gray-800 whitespace-nowrap text-sm">{label}:</span>
      <span className="text-gray-900 font-bold text-sm">{value || '—'}</span>
    </div>
  );

  return (
    <>
      <style>{`
        @media print {
          @page { size: letter; margin: 0.4in; }
          html, body {
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .no-print { display: none !important; }
          .avoid-break { page-break-inside: avoid; }
          body * { visibility: hidden; }
          #${printContentId}, #${printContentId} * { visibility: visible; }
          /* No position:absolute here anymore — it pulled this element out of
             normal document flow, so the browser's print engine had nothing
             left in flow to measure and clipped multi-page content to a
             single page instead of paginating it (this element's siblings
             are already removed from layout via display:none at print time,
             see the beforeprint/afterprint isolation above, so this no
             longer needs position tricks to avoid printing the page behind
             the modal — it just needs to stay in flow like the standalone
             /print route, which never used position:absolute either). */
          .print-root {
            min-height: unset !important;
            padding: 0 !important;
          }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
        }
      `}</style>

      <div ref={rootRef} id={printContentId} className="print-root relative min-h-0 bg-white p-8 max-w-[740px] mx-auto">

        <div ref={contentRef}>

        {/* ── HEADER ── */}
        <div className="border-2 border-gray-800 mb-4 avoid-break">
          <div className="grid grid-cols-12 border-b-2 border-gray-800">
            <div className="col-span-3 p-3 border-r-2 border-gray-800 flex items-center justify-center">
              {tenantLogo
                ? <img src={tenantLogo} alt="Logo" className="max-h-14 max-w-full object-contain" />
                : <div className="text-2xl font-bold text-gray-500">LOGO</div>}
            </div>
            <div className="col-span-6 p-3 border-r-2 border-gray-800 flex items-center justify-center">
              <h1 className="text-xl font-bold text-center tracking-wider">ORDEN DE PRODUCCIÓN</h1>
            </div>
            <div className="col-span-3 p-0">
              <div className="grid grid-cols-2 border-b border-gray-800">
                <div className="px-1 py-1 border-r border-gray-800">
                  <div className="text-[9px] font-semibold text-gray-500 whitespace-nowrap">CÓDIGO:</div>
                  <div className="text-[10px] font-bold whitespace-nowrap">4-PRO-026</div>
                </div>
                <div className="px-1 py-1">
                  <div className="text-[9px] font-semibold text-gray-500 whitespace-nowrap">EDICIÓN:</div>
                  <div className="text-[10px] font-bold whitespace-nowrap">0-280525</div>
                </div>
              </div>
              <div className="px-2 py-1">
                <div className="text-xs font-semibold text-gray-500">FECHA:</div>
                <div className="text-sm">{new Date(order.createdAt).toLocaleDateString('es-MX')}</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 text-sm border-b border-gray-800">
            <div className="p-2 border-r border-gray-800">
              <span className="font-bold italic">LOTE: </span>
              <span className="italic">{order.lotCode || 'N/A'}</span>
            </div>
            <div className="p-2 text-right">
              <span className="font-bold italic">ORDEN No. </span>
              <span className="italic font-bold">{order.orderNumber}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 text-xs">
            <div className="p-2 border-r border-gray-800">
              <span className="font-semibold text-gray-600">PESO ESTRUCTURA (gr/m²): </span>
              <span className="font-bold">{structureWeight ? parseFloat(structureWeight).toFixed(4) : '—'}</span>
            </div>
            <div className="p-2 border-r border-gray-800 text-center">
              <span className="font-semibold text-gray-600">RENDIMIENTO (m²/kg): </span>
              <span className="font-bold">{rendimiento ? parseFloat(rendimiento).toFixed(4) : '—'}</span>
            </div>
            <div className="p-2 text-right">
              <span className="font-semibold text-gray-600">FACTOR DESPERDICIO (%): </span>
              <span className="font-bold">{factorDesperdicio != null && factorDesperdicio !== '' ? parseFloat(factorDesperdicio).toFixed(2) : '—'}</span>
            </div>
          </div>
        </div>

        {/* ── INFORMACIÓN GENERAL ── */}
        <div className="grid grid-cols-2 gap-x-6 mb-4 avoid-break text-xs">
          <div className="space-y-0.5">
            <BigRow label="CLIENTE" value={order.customerName} />
            <Row label="CÓDIGO DE CLIENTE" value={order.customerCode} />
            <BigRow label="PRODUCTO" value={order.productName} />
            <Row label="CÓDIGO DE PRODUCTO" value={order.productCode} />
            <Row label="TIPO DE PROCESO" value={parsedNotes.processType} />
            <Row label="O. DE COMPRA / SOLICITUD" value={parsedNotes.purchaseOrder} />
          </div>
          <div className="space-y-0.5">
            <Row label="FECHA O. DE COMPRA" value={parsedNotes.purchaseDate} />
            <Row label="FECHA DE PRODUCCIÓN" value={order.plannedStartDate} />
            <Row label="FECHA DE ENTREGA" value={parsedNotes.deliveryDate || order.dueDate} />
            <Row label="FECHA COMPROMISO" value={parsedNotes.commitmentDate} />
            <Row label="EQUIPO PREPRENSA" value={(order as any).equipoPreprensa} />
            <Row label="CANTIDAD" value={`${order.quantityPlanned} ${order.unit}`} />
            {/* ROT-215: same BigRow format as CLIENTE, printed right below CANTIDAD */}
            {(order as any).branchName && (
              <BigRow label="SUCURSAL" value={(order as any).branchName} />
            )}
          </div>
        </div>

        <div className="mb-3 text-xs flex items-center gap-3 avoid-break">
          <span className="font-semibold">TIPO DE PEDIDO:</span>
          <span className="font-bold text-gray-900 bg-gray-100 border border-gray-400 px-2 py-0.5 rounded">
            {parsedNotes.orderType === 'NUEVO' ? 'PRODUCTO NUEVO'
              : parsedNotes.orderType === 'REPETICION' ? 'REPETICIÓN'
              : parsedNotes.orderType === 'PRODUCTO_NUEVO' ? 'PRODUCTO NUEVO'
              : '—'}
          </span>
        </div>

        {order.label && (
          <div className="mb-4 p-2 bg-orange-50 border-2 border-orange-400 rounded text-center avoid-break">
            <div className="text-3xl font-bold text-orange-600 uppercase">{order.label}</div>
          </div>
        )}

        {/* ── ESTRUCTURA (MATERIALES) ── */}
        {materials && materials.length > 0 && (
          <div className="mb-4">
            <SectionHeader num="1" title="Estructura de Materiales" />
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-700 px-2 py-1 text-left">MATERIAL</th>
                  <th className="border border-gray-700 px-2 py-1 text-center">CALIBRE</th>
                  <th className="border border-gray-700 px-2 py-1 text-center">gr/m²</th>
                  <th className="border border-gray-700 px-2 py-1 text-center">%</th>
                  <th className="border border-gray-700 px-2 py-1 text-right">CANTIDAD</th>
                  <th className="border border-gray-700 px-2 py-1 text-center">MEDIDA</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((mat, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? '' : 'bg-gray-50'}>
                    <td className="border border-gray-700 px-2 py-1">{mat.material || mat.itemName}</td>
                    <td className="border border-gray-700 px-2 py-1 text-center">{mat.caliber || '—'}</td>
                    <td className="border border-gray-700 px-2 py-1 text-center">{mat.grm2 || '—'}</td>
                    <td className="border border-gray-700 px-2 py-1 text-center">{mat.percentage || '—'}</td>
                    <td className="border border-gray-700 px-2 py-1 text-right">{mat.quantity} {mat.unit}</td>
                    <td className="border border-gray-700 px-2 py-1 text-center">{mat.medida || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── SECCIÓN 3: ESPECIFICACIONES DEL PROCESO ── */}
        <div className="mb-4">
          <SectionHeader num="3" title="Especificaciones del Proceso" />

          <div className="grid grid-cols-2 gap-x-6 text-xs mb-3">
            <div className="space-y-0.5">
              <Row label="ANCHO INDIVIDUAL" value={processSpecs.individualWidth ? `${processSpecs.individualWidth} mm` : null} />
              <Row label="BOBINAS AL EJE" value={processSpecs.bobinCount ? `${processSpecs.bobinCount} pzas` : null} />
              <Row label="ANCHO DE IMPRESIÓN" value={processSpecs.printWidth ? `${processSpecs.printWidth} mm` : null} />
              <Row label="ANCHO DE REEFILE" value={processSpecs.reelWidth ? `${processSpecs.reelWidth} mm` : null} />
              <Row label="ANCHO DEL CORTE P/BOLSEO" value={processSpecs.baggingCutWidth || null} />
              {/* ROT-214: shown right below Ancho del Corte P/Bolseo, matching
                  its position in the edit form, only for Stand Pouch orders. */}
              {processSpecs.refileStandPouch && (
                <Row label="REFILE P/BOLSA STAND POUCH" value={`${processSpecs.refileStandPouch} mm`} />
              )}
              <Row label="ANCHO DEL SUSTRATO" value={processSpecs.substrateWidth ? `${processSpecs.substrateWidth} mm` : null} />
              {(() => {
                const rawBarCodes = processSpecs.barCode || parsedNotes.codigoBarras || (order as any).codigoBarras;
                // ROT-177 follow-up: several codes are stored as one
                // comma-separated string (one roll, several designs/flavors)
                // — print each on its own line, in the order they were
                // captured, instead of as running inline text.
                const barCodes = typeof rawBarCodes === 'string'
                  ? rawBarCodes.split(',').map(c => c.trim()).filter(Boolean)
                  : [];
                return barCodes.length > 0 ? (
                  <div className="mb-0.5 text-xs">
                    <span className="font-semibold text-gray-700">CÓDIGO DE BARRAS:</span>
                    <ul className="mt-0.5 ml-3 list-disc">
                      {barCodes.map((code, i) => (
                        <li key={i} className="text-gray-900">{code}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <Row label="CÓDIGO DE BARRAS" value={null} />
                );
              })()}
            </div>
            <div className="space-y-0.5">
              <Row label="REPETICIÓN INDIVIDUAL" value={processSpecs.individualRepetition ? `${processSpecs.individualRepetition} mm` : null} />
              <Row label="No. DE REPETICIONES" value={processSpecs.repetitionCount} />
              <Row label="CIRCUNFERENCIA DEL CILINDRO" value={processSpecs.cylinderCircumference ? `${processSpecs.cylinderCircumference} mm` : null} />
              <Row label="NÚMERO DE CILINDROS" value={processSpecs.cylinderCount} />
              <div className="mb-0.5 flex items-center gap-3 text-xs">
                <span className="font-semibold">LÍNEA DE CORTE:</span>
                <span className="font-bold text-gray-900">{processSpecs.cutLine ? 'SÍ' : 'NO'}</span>
                <span className="font-semibold ml-3">PIE DE CASA:</span>
                <span className="font-bold text-gray-900">{processSpecs.footHouse ? 'SÍ' : 'NO'}</span>
              </div>
              <div className="text-xs">
                <span className="font-semibold">FOTOCELDA:</span>
                {' '}ANCHO: {processSpecs.photocell?.width || '—'} mm · LARGO: {processSpecs.photocell?.height || '—'} mm
              </div>
            </div>
          </div>

          <div className="mb-2 text-xs flex items-center gap-2">
            <span className="font-semibold">REPETICIÓN CONTINUA:</span>
            <span className="font-bold text-gray-900">{processSpecs.continuousRepetition ? 'SÍ' : 'NO'}</span>
          </div>

          {activeColorRows.length > 0 && (
            <div className="mb-2">
              <div className="text-xs font-semibold text-gray-700 mb-1">COLORES / ESCALA PANTONE:</div>
              <table className="border-collapse text-xs w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-600 px-2 py-1 w-8">#</th>
                    <th className="border border-gray-600 px-2 py-1">COLOR BASE</th>
                    <th className="border border-gray-600 px-2 py-1">PANTONE</th>
                    <th className="border border-gray-600 px-2 py-1 w-8">#</th>
                    <th className="border border-gray-600 px-2 py-1">COLOR BASE</th>
                    <th className="border border-gray-600 px-2 py-1">PANTONE</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: Math.ceil(activeColorRows.length / 2) }).map((_, i) => {
                    const left = activeColorRows[i * 2];
                    const right = activeColorRows[i * 2 + 1];
                    return (
                      <tr key={i}>
                        <td className="border border-gray-600 px-2 py-0.5 text-center text-gray-500">{i * 2 + 1}</td>
                        <td className="border border-gray-600 px-2 py-0.5">{left?.colorBase || ''}</td>
                        <td className="border border-gray-600 px-2 py-0.5">{left?.pantone || ''}</td>
                        <td className="border border-gray-600 px-2 py-0.5 text-center text-gray-500">{right ? i * 2 + 2 : ''}</td>
                        <td className="border border-gray-600 px-2 py-0.5">{right?.colorBase || ''}</td>
                        <td className="border border-gray-600 px-2 py-0.5">{right?.pantone || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        </div>

        {/* ── SECCIÓN 3.1: LAMINACIÓN ── */}
        {(processSpecs.adhesiveLamination || processSpecs.laminationCatalyst ||
          processSpecs.trilaminationAdhesive || processSpecs.trilaminationCatalyst) && (
          <div className="mb-4 avoid-break">
            <SubSectionHeader num="3.1" title="Laminación" />
            <table className="w-full border-collapse text-xs">
              <tbody>
                <tr>
                  <td className="border border-gray-600 bg-gray-50 px-2 py-1.5 font-semibold w-48">ADHESIVO LAMINACIÓN</td>
                  <td className="border border-gray-600 px-2 py-1.5">{processSpecs.adhesiveLamination || '—'}</td>
                  <td className="border border-gray-600 bg-gray-50 px-2 py-1.5 font-semibold w-48">CATALIZADOR LAMINACIÓN</td>
                  <td className="border border-gray-600 px-2 py-1.5">{processSpecs.laminationCatalyst || '—'}</td>
                </tr>
                <tr>
                  <td className="border border-gray-600 bg-gray-50 px-2 py-1.5 font-semibold">ADHESIVO TRI LAMINACIÓN</td>
                  <td className="border border-gray-600 px-2 py-1.5">{processSpecs.trilaminationAdhesive || '—'}</td>
                  <td className="border border-gray-600 bg-gray-50 px-2 py-1.5 font-semibold">CATALIZADOR TRILAMINADOS</td>
                  <td className="border border-gray-600 px-2 py-1.5">{processSpecs.trilaminationCatalyst || '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* ── SECCIÓN 3.2: EMPAQUE ── */}
        <div className="mb-4 avoid-break">
          <SubSectionHeader num="3.2" title="Empaque" />
          <div className="border border-gray-600 px-3 py-2 text-xs min-h-[40px]">
            {processSpecs.packaging || <span className="text-gray-400 italic">Sin instrucciones de empaque</span>}
          </div>
        </div>

        {/* ── SECCIÓN 5: CONFIGURACIÓN DE IMPRESIÓN ── */}
        {(processSpecs.innerDiameter || processSpecs.outerDiameter || processSpecs.windingDirection || order.printConfigImage) && (
          <div className="mb-4 avoid-break">
            <SectionHeader num="5" title="Configuración de Impresión" />

            <div className="flex gap-4 items-start">
              <div className="flex-1 min-w-0">
                <table className="w-full border-collapse text-xs">
                  <tbody>
                    <tr>
                      <td className="border border-gray-600 bg-gray-50 px-2 py-1.5 font-semibold w-44">FIGURA No.</td>
                      <td className="border border-gray-600 px-2 py-1.5">{order.printConfigImage || '—'}</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-600 bg-gray-50 px-2 py-1.5 font-semibold w-44">DIÁMETRO INTERIOR</td>
                      <td className="border border-gray-600 px-2 py-1.5">
                        {processSpecs.innerDiameter || '—'} {processSpecs.innerDiameter ? (processSpecs.innerDiameterUnit || 'mm') : ''}
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-600 bg-gray-50 px-2 py-1.5 font-semibold">DIÁMETRO EXTERIOR</td>
                      <td className="border border-gray-600 px-2 py-1.5">
                        {processSpecs.outerDiameter || '—'} {processSpecs.outerDiameter ? (processSpecs.outerDiameterUnit || 'mm') : ''}
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-600 bg-gray-50 px-2 py-1.5 font-semibold">SENTIDO EMBOBINADO FINAL</td>
                      <td className="border border-gray-600 px-2 py-1.5">{processSpecs.windingDirection || '—'}</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-600 bg-gray-50 px-2 py-1.5 font-semibold">LADO FOTOCELDA</td>
                      <td className="border border-gray-600 px-2 py-1.5">{processSpecs.photocellSide || 'CENTRADA'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {order.printConfigImage && (
                <div className="flex-shrink-0 w-64">
                  <div className="flex justify-center items-center border border-gray-300 rounded bg-gray-50 p-2 min-h-[180px]">
                    <img
                      src={urlFor(order.printConfigImage)}
                      alt={`Configuración de Impresión ${order.printConfigImage}`}
                      style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                        (e.currentTarget.parentElement as HTMLElement).innerHTML =
                          '<p style="color:#9ca3af;font-size:12px;text-align:center">Imagen no disponible</p>';
                      }}
                    />
                  </div>
                  <div className="text-center text-xs text-gray-500 mt-1">
                    Configuración seleccionada: {order.printConfigImage}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {parsedNotes.observations && (
          <div className="mb-4 avoid-break">
            <SectionHeader num="6" title="Observaciones" />
            <div className="text-xs border border-gray-600 px-3 py-2 whitespace-pre-wrap">
              {parsedNotes.observations}
            </div>
          </div>
        )}

        {/* ROT-207: general (not per-process) note captured from Plant
            Execution — must keep showing here even once the order is
            CLOSED, since this component is also the reprint view. */}
        {parsedNotes.plantExecutionObservations && (
          <div className="mb-4 avoid-break">
            <SectionHeader num="7" title="Observaciones Generales de Producción" />
            <div className="text-xs border border-gray-600 px-3 py-2 whitespace-pre-wrap">
              {parsedNotes.plantExecutionObservations}
            </div>
          </div>
        )}

        {/* ROT-208: general (not per-process) note captured from Cierre de
            Producción — same always-visible-once-CLOSED requirement as
            plantExecutionObservations above. */}
        {parsedNotes.closureGeneralObservations && (
          <div className="mb-4 avoid-break">
            <SectionHeader num="8" title="Observaciones Generales al Cierre de Producción" />
            <div className="text-xs border border-gray-600 px-3 py-2 whitespace-pre-wrap">
              {parsedNotes.closureGeneralObservations}
            </div>
          </div>
        )}

        <div className="mt-8 grid grid-cols-3 gap-6 avoid-break">
          {['ELABORÓ', 'REVISÓ', 'AUTORIZÓ'].map((label) => (
            <div key={label} className="text-center">
              <div className="border-t-2 border-gray-800 pt-2">
                <div className="text-xs font-semibold text-gray-700">{label}</div>
              </div>
            </div>
          ))}
        </div>

        </div>

        {/* Page footers: folio + "Página X de Y", repeated at every estimated page boundary */}
        <div className="hidden print:block">
          {Array.from({ length: pageCount }, (_, i) => (
            <div
              key={i}
              className="absolute left-0 right-0 flex justify-between items-center text-[9px] text-gray-500 border-t border-gray-300 pt-1"
              style={{ top: (i + 1) * PAGE_CONTENT_HEIGHT_PX - 20 }}
            >
              <span>Folio: {order.orderNumber}</span>
              <span>Página {i + 1} de {pageCount}</span>
            </div>
          ))}
        </div>

      </div>
    </>
  );
}
